import 'pure-swipe';
import { render } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { ModalWrapper } from './components/modal-wrapper';
import { ModalContent } from './components/modal-content';
import { GlobalEvents } from '@enums';
import { getWidgetLocation } from '@utils/helper-functions';

class GlobalModalElement extends HTMLElement {
  readonly _root: ShadowRoot;
  private _impactOffer: any;

  constructor() {
    super();
    this._root = this.attachShadow({ mode: 'open' });
  }

  set impactOffer(value: string[]) {
    this._impactOffer = value;
    this.renderComponent();
  }

  get impactOffer() {
    return this._impactOffer;
  }

  connectedCallback() {
    this.renderComponent();
  }

  disconnectedCallback() {
    render(null, this._root);
  }

  private renderComponent() {
    render(<GlobalModal impactOffer={this._impactOffer} />, this._root);
  }
}

customElements.define('inspired-global-modal', GlobalModalElement);

const GlobalModal = ({ impactOffer }: any) => {
  const mixpanel = (window as any).inspiredMixpanel;
  const platformStoreId = (window as any).inspiredStoreId;
  const { causeName, charityName, storeName } = impactOffer;

  const [isVisible, setIsVisible] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const [widgetState, setWidgetState] = useState({} as any);
  const [stateHandler, setStateHandler] = useState<any>(null);

  useEffect(() => {
    const initializeState = async () => {
      const [{ }, stateHandler] = await (window as any).inspiredDataPromise;
      setStateHandler(stateHandler);
    };

    initializeState();
  }, []);

  useEffect(() => {
    if (!stateHandler) return;

    const updateState = () => {
      setWidgetState(stateHandler.getState());
    };

    updateState();

    stateHandler.subscribe(updateState);
    return () => {
      stateHandler.unsubscribe(updateState);
    };
  }, [stateHandler]);

  const { calculatedDonation } = widgetState;

  // future
  const [selectedCause, setSelectedCause] = useState<string | undefined>();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data && event.data.type === GlobalEvents.ToggleModal) {
        if (event.data.isVisible) {
          const body = document.querySelector('body');
          if (body) body.style.overflow = 'hidden';
        } else {
          const body = document.querySelector('body');
          if (body) body.style.overflow = 'auto';
        }

        const isVisible = event.data.isVisible;

        // handle view event of first slide
        if (isVisible) sendMixpanelEvent(1);

        setIsVisible(isVisible);
      }

      // future
      if (event.data && event.data.type === GlobalEvents.SelectCause) {
        setSelectedCause(event.data.cause);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleClose = (event: MouseEvent) => {
    if (
      event.target === event.currentTarget ||
      !!(event.target as HTMLElement).closest('.modal-close')
    ) {
      window.postMessage(
        { type: GlobalEvents.ToggleModal, isVisible: false },
        window.location.origin
      );
      setIsVisible(false);
      setSlideIndex(0);
      const body = document.querySelector('body');
      if (body) body.style.overflow = 'auto';
    }
  };

  const handleNavigation = (i: number) => {
    setSlideIndex(i);
    sendMixpanelEvent(i + 1);
  };

  const sendMixpanelEvent = (slideNumber: number) => {
    const widgetLocation = getWidgetLocation();
    mixpanel?.track('global_modal_viewed', {
      causeArea: causeName,
      charityName,
      // marketingCampaignId: '',
      modalLocation: widgetLocation,
      platformStoreId,
      screenViewed: slideNumber,
      storeName
    });
  }

  const handlePrev = useCallback(() => {
    if (isVisible && slideIndex > 0) handleNavigation(slideIndex - 1);
  }, [isVisible, slideIndex]);

  const handleNext = useCallback(() => {
    if (isVisible && slideIndex < 4) handleNavigation(slideIndex + 1);
  }, [isVisible, slideIndex]);

  useEffect(() => {
    document.addEventListener('swiped-right', handlePrev);
    document.addEventListener('swiped-left', handleNext);
    return () => {
      document.removeEventListener('swiped-right', handlePrev);
      document.removeEventListener('swiped-left', handleNext);
    };
  }, [handleNext, handlePrev]);

  return (
    <div class={isVisible ? '' : 'global-modal-hidden'}>
      <ModalWrapper onClose={handleClose}>
        <ModalContent
          calculatedDonation={calculatedDonation}
          impactOffer={impactOffer}
          slideIndex={slideIndex}
          onSlideIndexChange={(i: number) => handleNavigation(i)}
          onClose={handleClose}
        />
      </ModalWrapper>
    </div>
  );
};

export default GlobalModalElement;
